<template>
	<AppPage class="p-6 py-5 gap-8" :class="{ 'dropping-files': isOverDropZone }">
		<AppOverlay v-if="isOverDropZone" />
		<AppCardPage>
			<h1 class="text-3xl">Hi {{ userStore.user?.fullName }}</h1>

			<template v-if="requestedDocuments && requestedDocuments?.length > 0">
				<h2 class="text-xl">Gevraagde documenten</h2>
				<AppCard>
					<ul v-if="requestedDocuments">
						<DocumentUploadListItem
							v-for="doc in requestedDocuments"
							:key="doc.id"
							:doc
							:docs-to-pick="fileUploadStore.existingFiles"
							@dropped="([file]) => droppedOnDoc(file, doc)"
							@picked="(file) => connectFile(file.id, doc)"
						/>
					</ul>
				</AppCard>
			</template>

			<div v-else ref="documents-to-connect-card" class="flex flex-col gap-6">
				<CustomerDocumentUploadingInstructions />
				<div class="flex flex-col gap-1">
					<h2 class="text-xl">Bestanden</h2>
					<FileOverviewLayout />
				</div>
			</div>
		</AppCardPage>
	</AppPage>
</template>

<script setup lang="ts">
import { acceptedMimeTypes, nameFromLocalPath } from "@/helpers/fileHelpers";
import { useDossierStore } from "@/store/useDossierStore";
import { useFileUploadStore } from "@/store/useFileUploadStore";
import { useUserStore } from "@/store/useUserStore";
import { useDropZone } from "@vueuse/core";
import { computed, useTemplateRef } from "vue";
import CustomerDocumentUploadingInstructions from "./CustomerDocumentUploadingInstructions.vue";
import type { HdnRequestedDocument } from "@/composables/useLoanApplicationSortedDocuments";
import { useQuery, useZero } from "@/composables/useZero";
import { mutators, queries } from "@loanlink-nl/zero";

const userStore = useUserStore();
const fileUploadStore = useFileUploadStore();
fileUploadStore.clearFilesToUpload();
const dossierStore = useDossierStore();

const documentsToConnectCard = useTemplateRef("documents-to-connect-card");
const { isOverDropZone } = useDropZone(() => documentsToConnectCard.value, {
	multiple: true,
	dataTypes: acceptedMimeTypes,
	onDrop: (files) => {
		if (!files?.length) {
			return;
		}

		void uploadFiles(files);
	},
});

const z = useZero();
const { data } = useQuery(() =>
	queries.loanApplications.forDossier({
		dossierId: dossierStore.selectedDossier?.id ?? "",
		orderField: "createdAt",
		order: "desc",
	}),
);

const requestedDocuments = computed(() =>
	data.value?.flatMap((doc) => doc.requestedDocuments),
);

async function uploadFiles(files: File[]) {
	fileUploadStore.addFilesToFilesToUpload(files);
	await fileUploadStore.uploadFiles();
	fileUploadStore.clearFilesToUpload();
}

async function droppedOnDoc(file: File | undefined, doc: HdnRequestedDocument) {
	if (!file) {
		return;
	}

	await uploadAndConnectFile(file, doc);
}

function connectFile(fileId: string, doc: HdnRequestedDocument) {
	const requestedDocToConnect = requestedDocuments.value?.find(
		(d) => d.id === doc.id,
	);
	if (!requestedDocToConnect || !userStore.user) {
		return;
	}

	z.value.mutate(
		mutators.hdnRequestedDocument.connect({
			id: doc.id,
			documentId: fileId,
			actionId: crypto.randomUUID(),
		}),
	);
}

async function uploadAndConnectFile(file: File, doc: HdnRequestedDocument) {
	const filename = nameFromLocalPath(file.name);
	const dossierDocumentId = await fileUploadStore.uploadFile({
		file,
		newName: filename,
	});

	connectFile(dossierDocumentId, doc);
	fileUploadStore.clearFilesToUpload();
}
</script>
